import {
  chosenSelectTerms,
  selectTermsDigest,
  type DbtJobRunRow,
} from "@shared/types/transformations";

/**
 * Round 6, T1: what the Run dialog says about the instance's one dbt slot, from the recent
 * transform steps (`GET /transformations/runs`).
 *
 * - `holder`: the step transforming now (the runner admits one at a time), with when it started.
 * - `waiting`: steps waiting for the slot (`queuedBehindRunId`) or about to claim it (a pending
 *   transform-only run), so a new run would wait too.
 * - `duplicateRunId`: a transform-only run of exactly this selection that has not started dbt
 *   yet, which the server refuses to start again (409 `TRANSFORM_ALREADY_QUEUED`, the same rule
 *   as `assertNotAlreadyQueued`: the same set of pinned ids, order and repeats ignored; for a run
 *   of chosen models, the same terms of its selection).
 */
export interface TransformQueue {
  holder: { runId: string; startedAt: string } | null;
  waiting: number;
  duplicateRunId: string | null;
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  const left = new Set(a);
  const right = new Set(b);
  if (left.size !== right.size) return false;
  for (const id of left) if (!right.has(id)) return false;
  return true;
}

/**
 * `requestSelect` is the `--select` of a run of chosen models (round 6, T3), compared by its
 * terms (`chosenSelectTerms`, order and repeats ignored), as `assertNotAlreadyQueued` does; a run
 * of transformations passes none and is compared by its ids.
 */
export function transformQueueOf(
  rows: readonly DbtJobRunRow[],
  requestIds: readonly string[],
  requestSelect: string | null = null,
): TransformQueue {
  const running = rows.find((row) => row.stepStatus === "running");
  const requestTerms = chosenSelectTerms(requestSelect);
  const duplicate = rows.find((row) => {
    if (row.stepStatus !== "pending" || !row.transformOnly) return false;
    if (requestTerms) {
      // Round 7: a list row may carry only a preview of a long selection; its digest stands in.
      if (row.adhocSelectDigest) return row.adhocSelectDigest === selectTermsDigest(requestTerms);
      const rowTerms = chosenSelectTerms(row.adhocSelect);
      return rowTerms !== null && sameSet(rowTerms, requestTerms);
    }
    return (
      row.adhocSelect === undefined &&
      row.pinnedDbtJobIds !== undefined &&
      requestIds.length > 0 &&
      sameSet(row.pinnedDbtJobIds, requestIds)
    );
  });
  return {
    holder: running
      ? { runId: running.runId, startedAt: running.startedAt ?? running.createdAt }
      : null,
    // A transform-only run still pending and not yet refused counts too: its start-transform is
    // on the queue, so it claims the slot within seconds (r1 N6). A scheduled run's pending
    // transform waits for its extracts, not for the slot, so it does not.
    waiting: rows.filter(
      (row) =>
        row.stepStatus === "pending" &&
        (row.queuedBehindRunId ||
          (row.transformOnly && (row.runStatus === "queued" || row.runStatus === "running"))),
    ).length,
    duplicateRunId: duplicate?.runId ?? null,
  };
}
